import { Component, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { Route, withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import theme from './theme';
import ConnectedPayment from './containers/Payment';
import ConnectedIntroSummary from './containers/IntroSummary';
import ConnectedReviewSummary from './containers/ReviewSummary';
import ProgressStepper from './components/ProgressStepper/ProgressStepper';
import { Banner, Header, Footer } from './components';
import ConnectedOrderSuccess from './components/OrderSuccess/OrderSuccess';
import ConnectedError from './containers/Error';
import { actions as paymentActions } from './reducers/payment';
import { actions as guestActions, selectGuestName, selectProgressSteps } from './reducers/guest';
import { actions as orderActions, selectLocationName } from './reducers/order';
import { selectFatalError } from './reducers';
import ooeConstants from './constants';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import * as ldService from './services/launchDarkly';

const BANNER_TYPES = ['info', 'alert'];

export function WithLaunchDarkly(WrappedComponent) {
  function LDWrapper(props) {
    const { showLdEnabledModal = false, messagingBanner = { messages: [] } } = useFlags();
    const ldClient = useLDClient();

    useEffect(() => {
      if (ldClient) {
        ldService.setClient(ldClient);
      }
    }, [ldClient]);

    const bannerMessages = Array.isArray(messagingBanner?.messages)
      ? messagingBanner.messages.filter(
          (message) => message?.messageText && BANNER_TYPES.includes(message.type),
        )
      : [];

    return (
      <ThemeProvider theme={theme}>
        <>
          {bannerMessages.map((message) => (
            <Banner
              key={`${message.type}-${message.messageText}`}
              message={message.messageText}
              type={message.type}
            />
          ))}
          {showLdEnabledModal && <Banner message="LaunchDarkly flag enabled" type="info" />}
          <WrappedComponent {...props} showLdEnabledModal={showLdEnabledModal} ldClient={ldClient} />
        </>
      </ThemeProvider>
    );
  }

  return LDWrapper;
}

export class App extends Component {
  componentDidMount() {
    const { history } = this.props;
    const { location } = history;
    const { hash } = location;
    if (location.pathname !== '/') {
      history.push(`/${hash}`);
    }

    const { getGuestOauthSession, throwFatalError } = this.props;
    getGuestOauthSession();
    setTimeout(() => throwFatalError(), ooeConstants.THIRTY_MINUTES);
  }

  render() {
    const { location, userName, fatalError, throwFatalError, history, stepCompleted, progressSteps } =
      this.props;
    const thisPage = window.location.pathname;
    if (fatalError && thisPage !== ooeConstants.PATH_NAME.ERROR) {
      throwFatalError();
    }

    return (
      <ThemeProvider theme={theme}>
        <>
          <Header location={location} userName={userName} />
          <ProgressStepper history={history} stepCompleted={stepCompleted} steps={progressSteps} />
          <Route exact path="/" component={ConnectedIntroSummary} />
          <Route exact path="/payment" component={ConnectedPayment} />
          <Route exact path="/summary" component={ConnectedIntroSummary} />
          <Route exact path="/success" component={ConnectedOrderSuccess} />
          <Route exact path="/error" component={ConnectedError} />
          <Route exact path="/review" component={ConnectedReviewSummary} />
          <Footer />
        </>
      </ThemeProvider>
    );
  }
}

App.propTypes = {
  getGuestOauthSession: PropTypes.func.isRequired,
  location: PropTypes.string.isRequired,
  userName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  fatalError: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  throwFatalError: PropTypes.func.isRequired,
  stepCompleted: PropTypes.func.isRequired,
  history: PropTypes.objectOf(PropTypes.any).isRequired,
  progressSteps: PropTypes.arrayOf(PropTypes.object),
};

App.defaultProps = {
  userName: '',
  fatalError: null,
  progressSteps: [],
};

/* v8 ignore start */
function mapStateToProps(state) {
  return {
    location: selectLocationName(state),
    userName: selectGuestName(state),
    fatalError: selectFatalError(state),
    progressSteps: selectProgressSteps(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ ...guestActions, ...paymentActions, ...orderActions }, dispatch);
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(WithLaunchDarkly(App)));
